/** @jsxImportSource @emotion/react */
import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import "twin.macro";
import Alert from "../shared/Alert";
import { PrimaryButton, PrimaryLinkButton } from "../shared/Button";
import { ErrorMessage, FormGroup, Input, Label, RequiredAsterisk, Radio } from "../shared/Form";
import { useLanguage } from "../shared/useLanguage";
import { useSearchParams } from "../shared/useSearchParams";
import { useRegisterMutation } from "./AuthAPI";
import { AuthLayoutDescription, AuthLayoutHeader, AuthLayoutTitle, AuthLayoutTitleSpecialOffer } from "./AuthLayout";
import { passwordPolicy, PasswordPolicyChecker } from "./PasswordPolicyChecker";
import { LoginTermsOfUseConfirmation } from "./LoginTermsOfUseConfirmation";
import { InformationCircleIcon } from "@heroicons/react/outline";
import { useDisclosure } from "../shared/useDisclosure";
import { RegisterMetroHelperModal } from "./RegisterMetroHelperModal";
import PhoneInput, { isPossiblePhoneNumber } from 'react-phone-number-input'
import 'react-phone-number-input/style.css'

import { css, Global } from "@emotion/react";

const RegisterRoute = ({ organizationContext = "CLASSICAL" }) => {
  const lang = useLanguage();
  const { t } = useTranslation();
  // Campaign param can be passed using URL query params
  const { campaign, school, schoolName } = useSearchParams();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("FR");
  const formProps = useForm({
    defaultValues: {
      firstname: "",
      lastname: "",
      email: "",
      password: "",
      metroCustomerId: "",
      metroSalesId: "",
      language: lang,
      optionalSchoolUuid: school,
      schoolname: schoolName,
      campaign: campaign,
      establishmentOpening: "ALREADY_OPEN",
      establishmentNumber: "ONE_ESTABLISHMENT",
      establishmentRecruiting: "IS_RECRUITING",
      phoneNumber: {
        countryIsoCode: "",
        number: ""
      },
      organizationPlan: "FREE",
      organizationSubPlan: organizationContext
    },
  });
  const { handleSubmit, control, setValue, register, setError, clearErrors } = formProps;
  const { mutate: registerUser, status, error } = useRegisterMutation();
  const password = useWatch({ name: "password", control });

  const [areTermsOfUseAccepted, setAreTermsOfUseAccepted] = useState(false);
  const {
    isOpen: isRegisterMetroHelperModalOpen,
    onOpen: onOpenRegisterMetroHelperModal,
    onClose: onCloseRegisterMetroHelperModal
  } = useDisclosure(false);

  useEffect(() => {
    clearErrors("phoneNumber");
    setValue("phoneNumber", {
      countryIsoCode: countryCode,
      number: phoneNumber
    });
  }, [phoneNumber, countryCode, setValue]);

  const handleCountryChange = (country) => {
    setCountryCode(country);
  };

  const validatePhoneNumber = () => {
    if ("METRO" !== organizationContext) {
      return true;
    }
    if (!phoneNumber) {
      setError("phoneNumber", {
        type: "manual",
        message: t("shared.required")
      });
      return false;
    }
    else if (phoneNumber && !isPossiblePhoneNumber(phoneNumber)) {
      setError("phoneNumber", {
        type: "manual",
        message: t("auth.phonenumber-error")
      });
      return false;
    } else {
      clearErrors("phoneNumber");
      return true;
    }
  };

  const onSubmit = (data) => {
    if (validatePhoneNumber()) {
      registerUser(data);
    }
  };

  let loginLink = "/login";
  if ("METRO" === organizationContext) {
    loginLink = "/metro-login";
  } else if ("GUSTY" === organizationContext) {
    loginLink = "/gusty-login";
  }

  return (
    <>
      <Global
        styles={css({
          ".PhoneInput": {
            position: "relative",
          },
          ".PhoneInputCountry": {
            position: "absolute!important",
            left: 10,
            top: 12,
            zIndex: 1,
          },
          ".PhoneInputCountryIconImg": {
            display: "block",
          },
          ".PhoneInputInput": {
            'border-color': "#CBD5E1;",
            'border-radius': "0.375rem;",
            'padding-left': "50px"
          },
        })}
      />
      {"special-offer" === campaign && <Helmet title={t("auth.register-a-new-account-special-offer-1") + t("auth.register-a-new-account-special-offer-2") + t("auth.register-a-new-account-special-offer-3")} />}
      {"special-offer" !== campaign && <Helmet title={t("auth.register-a-new-account")} />}
      <AuthLayoutHeader organizationContext={organizationContext}>
        {"special-offer" === campaign && <AuthLayoutTitleSpecialOffer children1={t("auth.register-a-new-account-special-offer-1")} children2={t("auth.register-a-new-account-special-offer-2")} children3={t("auth.register-a-new-account-special-offer-3")}></AuthLayoutTitleSpecialOffer>}
        {"special-offer" !== campaign && <AuthLayoutTitle>{t("auth.register-a-new-account")}</AuthLayoutTitle>}
        <AuthLayoutDescription>
          <Trans i18nKey="auth.or-sign-in-to-your-account">
            Or{" "}
            <PrimaryLinkButton as={Link} to={loginLink}>
              sign in to your account
            </PrimaryLinkButton>
          </Trans>
        </AuthLayoutDescription>
      </AuthLayoutHeader>
      {status === "success" ? (
        <Alert
          tw="mt-8"
          variant="info"
          title={t("auth.registration-requested")}
          description={t("auth.registration-requested-description")}
        />
      ) : (
        <FormProvider {...formProps}>
          <form onSubmit={handleSubmit(onSubmit)} tw="space-y-6 my-8">
            {error && <Alert title={t(error.message)} />}

            <FormGroup>
              <Label>
                {t("auth.firstname")} <RequiredAsterisk />
              </Label>
              <Input name="firstname" autoComplete="given-name" rules={{ required: true }} />
              <ErrorMessage name="firstname" />
            </FormGroup>

            <FormGroup>
              <Label>
                {t("auth.lastname")} <RequiredAsterisk />
              </Label>
              <Input name="lastname" autoComplete="family-name" rules={{ required: true }} />
              <ErrorMessage name="lastname" />
            </FormGroup>

            <FormGroup>
              <Label>
                {t("auth.organization")} <RequiredAsterisk />
              </Label>
              <Input
                name="organizationName"
                autoComplete="organization"
                rules={{ required: true }}
              />
              <ErrorMessage name="organizationName" />
            </FormGroup>

            <FormGroup>
              <Label>
                {t("auth.email-address")} <RequiredAsterisk />
              </Label>
              <Input type="email" name="email" autoComplete="username" rules={{ required: true }} />
              <ErrorMessage name="email" />
            </FormGroup>

            {
              "METRO" === organizationContext &&
              <FormGroup>
                <Label htmlFor="phoneNumber">{t("auth.phonenumber")} <RequiredAsterisk /></Label>
                <PhoneInput
                  name="phoneNumber"
                  defaultCountry="FR"
                  onCountryChange={handleCountryChange}
                  value={phoneNumber}
                  onChange={setPhoneNumber}
                // error={phoneNumber ? (isPossiblePhoneNumber(phoneNumber) ? undefined : 'auth.phonenumber-error') : 'Phone number required'} 
                />
                <ErrorMessage name="phoneNumber" />
              </FormGroup>
            }

            {
              "METRO" === organizationContext &&
              <FormGroup>
                <Label tw="flex justify-between items-center w-full">
                  <span>{t("auth.metro-id")} {/*<RequiredAsterisk />*/}</span>
                  <InformationCircleIcon tw="w-5 hover:cursor-pointer" onClick={(e) => { e.preventDefault(); onOpenRegisterMetroHelperModal(true) }} />
                </Label>
                <Input
                  name="metroCustomerId"
                  autoComplete="metroCustomerId"
                  placeholder={t("auth.placeholder-metro-id")}
                  onKeyDown={(event) => {
                    if (!/[0-9]/.test(event.key) && event.key !== 'Backspace' && event.key !== 'Tab') {
                      event.preventDefault();
                    }
                  }}
                  rules={{
                    // required: true,
                    validate: {
                      lengthAndDigits: value =>
                        (!value || /^\d{13}$/.test(value) || /^\d{22}$/.test(value)) || t("auth.error-metro-id"),
                    }
                  }}
                />
                <ErrorMessage name="metroCustomerId" />
              </FormGroup>
            }

            {
              "METRO" === organizationContext &&
              <FormGroup>
                <Label>
                  {t("auth.metro-sales-id")}
                </Label>
                <Input
                  name="metroSalesId"
                  autoComplete="metroSalesId"
                />
                <ErrorMessage name="metroSalesId" />
              </FormGroup>
            }

            {
              ("METRO" === organizationContext || "GUSTY" === organizationContext) &&
              <>
                <FormGroup>
                  <Label>{t("auth.select-option-isopen")}</Label>
                  <div tw="flex flex-col space-y-2">
                    <label tw="pb-2">
                      <Radio
                        name="establishmentOpening"
                        value="ALREADY_OPEN"
                        tw="mr-2"
                      />
                      {t("auth.select-option-isopen-1")}
                    </label>
                    <label tw="pb-2">
                      <Radio
                        name="establishmentOpening"
                        value="OPENING"
                        tw="mr-2"
                      />
                      {t("auth.select-option-isopen-2")}
                    </label>
                    <label>
                      <Radio
                        name="establishmentOpening"
                        value="OPENING_LATER"
                        tw="mr-2"
                      />
                      {t("auth.select-option-isopen-3")}
                    </label>
                  </div>
                  <ErrorMessage name="establishmentOpening" />
                </FormGroup>
                <FormGroup>
                  <Label>{t("auth.select-option-ismultiple")}</Label>
                  <div tw="flex flex-col space-y-2">
                    <label tw="pb-2">
                      <Radio
                        name="establishmentNumber"
                        value="MULTIPLE_ESTABLISHMENT"
                        tw="mr-2"
                      />
                      {t("shared.yes")}
                    </label>
                    <label>
                      <Radio
                        name="establishmentNumber"
                        value="ONE_ESTABLISHMENT"
                        tw="mr-2"
                      />
                      {t("shared.no")}
                    </label>
                  </div>
                  <ErrorMessage name="establishmentNumber" />
                </FormGroup>
                <FormGroup>
                  <Label>{t("auth.select-option-isrecruiting")}</Label>
                  <div tw="flex flex-col space-y-2">
                    <label tw="pb-2">
                      <Radio
                        name="establishmentRecruiting"
                        value="IS_RECRUITING"
                        tw="mr-2"
                      />
                      {t("shared.yes")}
                    </label>
                    <label>
                      <Radio
                        name="establishmentRecruiting"
                        value="DONT_RECRUITING"
                        tw="mr-2"
                      />
                      {t("shared.no")}
                    </label>
                  </div>
                  <ErrorMessage name="establishmentRecruiting" />
                </FormGroup>
              </>
            }

            <FormGroup>
              <Label>
                {t("auth.password")} <RequiredAsterisk />
              </Label>
              <Input
                type="password"
                name="password"
                autoComplete="new-password"
                rules={{
                  required: true,
                  validate: {
                    passwordStrength: (value) => passwordPolicy.check(value),
                  },
                }}
              />
              <ErrorMessage name="password" />
            </FormGroup>
            <PasswordPolicyChecker password={password} />

            <LoginTermsOfUseConfirmation
              checked={areTermsOfUseAccepted}
              onChange={setAreTermsOfUseAccepted}
            />

            <PrimaryButton tw="w-full" type="submit" disabled={status === "loading"}>
              {t("auth.register-my-account")}
            </PrimaryButton>
          </form>
        </FormProvider>
      )}
      <RegisterMetroHelperModal isOpen={isRegisterMetroHelperModalOpen} onClose={onCloseRegisterMetroHelperModal} />
    </>
  );
};

export default RegisterRoute;
